import { Formik } from "formik";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { APIContext } from "../../../connector";
import { locationsApi, tripsApi, getErrorMessage } from "../../../api";
import { useToast } from "../../../hooks/useToast";
import classes from "./Booking.module.scss";
import Carousel from "../../../components/Carousel";
import Dropdown from "../../../components/Dropdown";
import FormCard from "../../../components/FormCard";
import RoundedEdgeButton from "../../../components/RoundedEdgeButton";
import RouteConnector from "../../../components/RouteConnector";
import { paxList } from "../../../utils/paxList";

const schema = Yup.object().shape({
  from_location: Yup.string().required("Pickup location is a required field"),
  to_location: Yup.string().required("Destination is a required field"),
  pax: Yup.number()
    .required("Pax is a required field")
    .integer("Input a valid number")
    .max(4, "Maximum of 4 passengers"),
});

const OPTIONS = { loop: true }
const SLIDE_COUNT = 5
const SLIDES = Array.from(Array(SLIDE_COUNT).keys())

const Booking = () => {
  const { userData } = useContext(APIContext);
  const { openToast } = useToast();
  const [locationsList, setLocationsList] = useState([]);
  //welcome message for specific users (to be removed later)
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [book, setBook] = useState(false);

  const userName = userData?.name || "User";

  const navigate = useNavigate();

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await locationsApi.getAll();

        const disabledLocations = ["TBS (Level 1)", "TBS (Main Lobby)"]
        const filteredData = data.filter(location => !disabledLocations.includes(location))

        const locationsList = [
          {
            options: filteredData.map((location) => ({
              label: location,
              value: location,
            })),
          },
        ];
        setLocationsList(locationsList);
      } catch (error) {
        console.error(error);
        openToast("Error fetching locations", 5000, "danger");
      }
    };

    fetchLocations();
  }, [openToast]);


  const handleSubmit = async (values) => {
    setBook(true);
    try {
      const data = await tripsApi.createTrip(values);
      const tripId = data.trip_id;
      navigate(`/searching-driver/${tripId}`);
    } catch (error) {
      console.error(error);
      const message = getErrorMessage(error);
      openToast(message || "Error creating trip", 5000, "danger");
      setBook(false);
    }
  };

  return (
    <>
      {/* Wrapping form inside formik tag and passing our schema to validationSchema prop */}
      <Formik
        validationSchema={schema}
        initialValues={{
          name: userName,
          from_location: "",
          to_location: "",
          pax: "1",
          prefer_private: false,
          request_female: false,
          have_luggage: false,
        }}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => {
          const pickupOption = locationsList[0]?.options.find(o => o.value === values.from_location) || null;
          // filter drop-off options so selected pickup isn't available
          const filteredDropoffOptions = locationsList.map(group => ({
            ...group,
            options: group.options.filter(opt => opt.value !== values.from_location)
          }));
          const dropoffOption = filteredDropoffOptions[0]?.options.find(o => o.value === values.to_location) || null;
          const filteredPickupOptions = locationsList.map(group => ({
            ...group,
            options: group.options.filter(opt => opt.value !== values.to_location)
          }));

          return (
            <div className={classes.container}>
              <Carousel />
              <div className={classes.innerContainer}>
                {/* Passing handleSubmit parameter tohtml form onSubmit property */}
                <form noValidate onSubmit={handleSubmit}>
                  {/* Our input html with passing formik parameters like handleChange, values, handleBlur to input properties */}
                  <FormCard className={classes.locationsCard}>
                    <div className={classes.locationsImage}>
                      <RouteConnector />
                    </div>
                    <div className={classes.locationsDetails}>
                      <Dropdown
                        value={pickupOption}
                        onChange={(value) => setFieldValue("from_location", value)}
                        options={filteredPickupOptions}
                        isSearchable
                        placeholder="From"
                        onBlur={() =>
                          handleBlur({ target: { name: "from_location" } })
                        }
                      />
                      {/* If validation is not passed show errors */}
                      <p className={classes.fieldError}>
                        {errors.from_location &&
                          touched.from_location &&
                          errors.from_location}
                      </p>
                      <Dropdown
                        value={dropoffOption}
                        onChange={(value) => setFieldValue("to_location", value)}
                        options={filteredDropoffOptions}
                        isSearchable
                        placeholder="To"
                        onBlur={() =>
                          handleBlur({ target: { name: "to_location" } })
                        }
                      />
                      {/* If validation is not passed show errors */}
                      {
                        <p className={classes.fieldError}>
                          {errors.to_location &&
                            touched.to_location &&
                            errors.to_location}
                        </p>
                      }
                    </div>
                  </FormCard>
                  {/* Our input html with passing formik parameters like handleChange, values, handleBlur to input properties */}
                  <FormCard className={classes.paxCard}>
                    <div className={classes.paxContainer}>
                      <p className={classes.title}>Pax</p>
                      <Dropdown
                        value={paxList.find((opt) => opt.value === values.pax)}
                        onChange={(value) => setFieldValue("pax", value)}
                        options={paxList}
                        placeholder="Number of Pax"
                        onBlur={() => handleBlur({ target: { name: "pax" } })}
                      />
                    </div>
                    {errors.pax && touched.pax && (
                      <p className={classes.fieldError}>
                        {errors.pax && touched.pax && errors.pax}
                      </p>
                    )}
                  </FormCard>

                  <FormCard className={classes.checkboxCard}>
                    <p className={classes.title}>Prefer private ride</p>
                    <input
                      id="prefer_private"
                      name="prefer_private"
                      className={classes.checkbox}
                      type="checkbox"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      defaultChecked
                    />
                  </FormCard>

                  <FormCard className={classes.checkboxCard}>
                    <p className={classes.title}>Have Luggage?</p>
                    <input
                      id="have_luggage"
                      name="have_luggage"
                      className={classes.checkbox}
                      type="checkbox"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormCard>

                  {userData?.gender === "F" && (
                    <FormCard className={classes.checkboxCard}>
                      <p className={classes.title}>Request Female Driver</p>
                      <input
                        id="request_female"
                        name="request_female"
                        className={classes.checkbox}
                        type="checkbox"
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </FormCard>
                  )}

                  {/* Click on submit button to submit the form */}
                  <RoundedEdgeButton
                    className={`acceptButton ${classes.bookButton}`}
                    type="submit"
                    disabled={book}
                  >
                    BOOK
                  </RoundedEdgeButton>
                </form>
              </div>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default Booking;
